import React, { Component } from 'react';
import { FaIcon } from '../FaIcon';
import { DataExplorerColumnsModal } from './DataExplorerColumnsModal';
import { withTranslation } from 'react-i18next';

/**
 * Botón "Columnas" del Explorador. Es la HERRAMIENTA de columnas (su propio menú), separada del
 * selector de Vistas. Acá viven los "layouts de columnas" (sub-concepto, solo columnas):
 *  - Configurar columnas… → abre el picker completo (DataExplorerColumnsModal).
 *  - Sugeridos (Dashbo, is_shared) + Mis layouts (personales) → aplican solo las columnas.
 *  - Guardar columnas como layout → guarda las columnas actuales.
 *
 * Las Vistas (snapshot completo: filtros + columnas + gráficos) viven en el SavedViewsSelector,
 * no acá. Así cada concepto tiene su superficie y no se mezclan.
 */
class FieldsSetsWidgetComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            modal: false,
            creatingNewSet: false,
            menuOpen: false,
        };
        this.wrapRef = React.createRef();
        this.closeModal = this.closeModal.bind(this);
        this.handleConfigureColumns = this.handleConfigureColumns.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside(event) {
        if (this.state.menuOpen && this.wrapRef.current && !this.wrapRef.current.contains(event.target)) {
            this.setState({ menuOpen: false });
        }
    }

    closeModal() {
        this.setState({ modal: false, creatingNewSet: false });
    }

    handleConfigureColumns() {
        this.setState({ modal: true, creatingNewSet: false, menuOpen: false });
    }

    handleApplyLayout(set) {
        this.setState({ menuOpen: false });
        if (this.props.onSelectFieldsSet) this.props.onSelectFieldsSet(set);
    }

    handleDeleteLayout(event, set) {
        event.stopPropagation();
        if (this.props.onDeleteFieldsSet) this.props.onDeleteFieldsSet(set);
    }

    getButtonText() {
        const { t, dimensions, metrics, editingFieldsSet } = this.props;
        const totalColumns = (dimensions?.length || 0) + (metrics?.length || 0);
        const columnsLabel = t('config_columns_widget.columns', 'Columnas');

        // Con layout activo, el botón muestra su nombre (es la herramienta dueña del concepto).
        if (editingFieldsSet && editingFieldsSet.name) {
            return totalColumns > 0
                ? `${columnsLabel}: ${editingFieldsSet.name} (${totalColumns})`
                : `${columnsLabel}: ${editingFieldsSet.name}`;
        }
        if (totalColumns > 0) {
            return `${columnsLabel} (${totalColumns})`;
        }
        return t('data_explorer.choose_columns', 'Elegir columnas');
    }

    renderModal() {
        if (!this.state.modal) return null;
        // En modo crear pasamos editingFieldsSet=null para que el modal guarde uno nuevo.
        const editingFieldsSet = this.state.creatingNewSet ? null : this.props.editingFieldsSet;
        return (
            <DataExplorerColumnsModal
                isOpen={this.state.modal}
                toggle={this.closeModal}
                onApply={this.props.onColumnsApply}
                dimensions={this.props.dimensions}
                metrics={this.props.metrics}
                fieldsData={this.props.fieldsData}
                sourceType={this.props.sourceType}
                fieldsSets={this.props.fieldsSets}
                editingFieldsSet={editingFieldsSet}
                onFieldsSetSaved={this.props.onFieldsSetSaved}
                clientHasGa4={this.props.clientHasGa4}
            />
        );
    }

    _renderLayoutRow(set, deletable) {
        const { editingFieldsSet } = this.props;
        const isActive = editingFieldsSet && String(editingFieldsSet.id) === String(set.id);
        return (
            <button
                key={set.id}
                type="button"
                className="columns-menu__layout"
                onClick={() => this.handleApplyLayout(set)}
            >
                <FaIcon iconName="table-columns" className="columns-menu__layout-icon" />
                <span className="columns-menu__layout-name">{set.name}</span>
                {isActive && <FaIcon iconName="check" className="columns-menu__layout-check" />}
                {deletable && set.created_by_user_id !== null && (
                    <FaIcon
                        iconName="trash"
                        className="columns-menu__layout-del"
                        onClick={(e) => this.handleDeleteLayout(e, set)}
                    />
                )}
            </button>
        );
    }

    renderMenu() {
        const { t, fieldsSets } = this.props;
        const sets = Array.isArray(fieldsSets) ? fieldsSets : [];
        const curated = sets.filter((s) => s.is_shared);
        const personal = sets.filter((s) => !s.is_shared);

        return (
            <div className="columns-menu">
                <button type="button" className="columns-menu__item" onClick={this.handleConfigureColumns}>
                    <FaIcon iconName="cog" style={{ marginRight: '0.5em' }} />
                    {t('data_explorer.configure_columns', 'Configurar columnas')}
                </button>

                {curated.length > 0 && (
                    <div className="columns-menu__section">
                        <div className="columns-menu__title">{t('fields_sets.section_suggested', 'Sugeridos')}</div>
                        {curated.map((s) => this._renderLayoutRow(s, false))}
                    </div>
                )}

                {personal.length > 0 && (
                    <div className="columns-menu__section">
                        <div className="columns-menu__title -muted">{t('fields_sets.section_mine', 'Mis sets de columnas')}</div>
                        {personal.map((s) => this._renderLayoutRow(s, true))}
                    </div>
                )}
            </div>
        );
    }

    render() {
        return (
            <div
                className="columns-button-wrapper"
                ref={this.wrapRef}
                style={{ position: 'relative', display: 'inline-block' }}
            >
                <button
                    className="btn -primary columns-button"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => this.setState({ menuOpen: !this.state.menuOpen })}
                >
                    <FaIcon iconName="columns" style={{ marginRight: '0.5rem' }} />
                    {this.getButtonText()}
                    <FaIcon iconName="chevron-down" style={{ marginLeft: '0.5rem', fontSize: '0.7em' }} />
                </button>
                {this.state.menuOpen && this.renderMenu()}
                {this.renderModal()}
            </div>
        );
    }
}

export const FieldsSetsWidget = withTranslation()(FieldsSetsWidgetComponent);
export default FieldsSetsWidget;
